import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { CreateParty, Language, PartyKind, type Party } from '@syndiqia/registry/contracts';
import { errorMessage, useApi } from '../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useParties, useRegistryMutation, useUnits } from '../queries';
import { useAllOwnership } from '../ledger-queries';
import { Link } from 'react-router';
import { useSyndicate } from '../session';

export function PartiesPage() {
  const { t } = useI18n();
  const syndicate = useSyndicate();
  const parties = useParties();
  const access = useAccess();
  const ownership = useAllOwnership();
  const units = useUnits();
  const unitsOf = (partyId: string) =>
    (ownership.data ?? [])
      .filter((o) => o.partyId === partyId)
      .map((o) => units.data?.find((u) => u.id === o.unitId)?.label ?? '')
      .filter(Boolean)
      .sort()
      .join(', ');

  if (!syndicate.can('registry.contacts.read')) {
    return (
      <>
        <PageHeader title={t.parties.title} />
        <Alert tone="info">{t.parties.boardOnly}</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t.parties.title}
        intro={t.parties.boardOnly}
        actions={syndicate.can('registry.contacts.write') && <Disclosure label={t.parties.add}>{(close) => <PartyForm onDone={close} />}</Disclosure>}
      />
      {parties.isPending && <Loading label={t.loading} />}
      {parties.isError && <Alert>{errorMessage(parties.error, t)}</Alert>}
      {parties.data?.length === 0 && <Empty>{t.parties.empty}</Empty>}
      {!!parties.data?.length && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{t.parties.displayName}</th>
              <th>{t.members.units}</th>
              <th>{t.parties.kind}</th>
              <th>{t.parties.email}</th>
              <th>{t.parties.phone}</th>
              <th>{t.parties.preferredLanguage}</th>
              <th>{t.access.title}</th>
            </tr>
          </thead>
          <tbody>
            {parties.data.map((p) => (
              <tr key={p.id}>
                <td className="cell-title">
                  <Link to={`${syndicate.base}/parties/${p.id}`}>{p.displayName}</Link>
                </td>
                <td data-label={t.members.units}>{unitsOf(p.id) || '—'}</td>
                <td data-label={t.parties.kind}>{t.parties.kinds[p.kind]}</td>
                <td data-label={t.parties.email}>{p.email ? <a href={`mailto:${p.email}`}>{p.email}</a> : '—'}</td>
                <td data-label={t.parties.phone}>{p.phone ?? '—'}</td>
                <td data-label={t.parties.preferredLanguage}>{t.parties.languages[p.preferredLanguage]}</td>
                <td data-label={t.access.title}>
                  <AccessCell party={p} status={access.data?.find((a) => a.partyId === p.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function PartyForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [values, setValues] = useState({ kind: 'person', displayName: '', email: '', phone: '', preferredLanguage: 'fr' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useRegistryMutation<CreateParty, Party>(
    (api, base, input) => api(`${base}/parties`, { method: 'POST', body: input }),
    ['parties'],
  );

  const set = (key: keyof typeof values) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(CreateParty, values);
    setErrors(result.errors ?? {});
    if (result.data) create.mutate(result.data, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={t.parties.kind} error={errors.kind}>
          <select value={values.kind} onChange={set('kind')}>
            {PartyKind.options.map((k) => (
              <option key={k} value={k}>
                {t.parties.kinds[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.parties.displayName} error={errors.displayName}>
          <input value={values.displayName} onChange={set('displayName')} autoFocus />
        </Field>
      </div>
      <div className="row">
        <Field label={t.parties.email} hint={t.common.optional} error={errors.email}>
          <input type="email" value={values.email} onChange={set('email')} />
        </Field>
        <Field label={t.parties.phone} hint={t.common.optional} error={errors.phone}>
          <input type="tel" value={values.phone} onChange={set('phone')} />
        </Field>
        <Field label={t.parties.preferredLanguage} error={errors.preferredLanguage}>
          <select value={values.preferredLanguage} onChange={set('preferredLanguage')}>
            {Language.options.map((l) => (
              <option key={l} value={l}>
                {t.parties.languages[l]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

export interface AccessStatus {
  partyId: string;
  userId: string | null;
  invitedAt: string | null;
  lastSeenAt: string | null;
  roles: string[];
}

export function useAccess() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'access'], queryFn: () => api<AccessStatus[]>(`${s.api}/access`) });
}

export function AccessCell({ party, status }: { party: Party; status: AccessStatus | undefined }) {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const invite = useRegistryMutation<void, { accountCreated: boolean; emailFailed: boolean }>(
    (api, base) => api(base.replace(/\/registry$/, '/invitations'), { method: 'POST', body: { partyId: party.id } }),
    ['access', 'comms'],
  );
  const label = status?.lastSeenAt
    ? t.access.active(date(status.lastSeenAt.slice(0, 10)))
    : status?.invitedAt
      ? t.access.invited(date(status.invitedAt.slice(0, 10)))
      : t.access.none;
  const canInvite = s.can('members.invite') && !status?.lastSeenAt && !status?.roles.some((r) => r === 'director' || r === 'manager');
  return (
    <div className="access-cell">
      <span className={status?.lastSeenAt ? 'badge badge-active' : status?.invitedAt ? 'badge badge-draft' : 'muted small'}>{label}</span>
      {canInvite &&
        (party.email ? (
          <button
            type="button"
            className="link-button small"
            disabled={invite.isPending || (invite.isSuccess && !invite.data?.emailFailed)}
            onClick={() => window.confirm(t.access.confirm(party.displayName)) && invite.mutate()}
          >
            {invite.isSuccess && !invite.data?.emailFailed ? t.access.sent : status?.invitedAt || invite.isSuccess ? t.access.reinvite : t.access.invite}
          </button>
        ) : (
          <span className="muted small">{t.access.noEmail}</span>
        ))}
      {invite.data?.emailFailed && <span className="field-error">{t.access.emailNotSent}</span>}
      {invite.isError && <span className="field-error">{errorMessage(invite.error, t)}</span>}
    </div>
  );
}
